/**
 * ADAPTED from pages/dev-agent/components/metadataEditor/sessionSaveWithValidation.ts.
 *
 * The dev-agent version ran a Salesforce validate→auto-fix loop against a
 * dev-agent SESSION (`validateAndSave`/`fixCode` on `../../devAgentService`).
 * The MULTI-AGENT IDE runs the SAME loop against the multi-agent RUN:
 *   - validate-save:  POST /api/runs/{run_id}/validate-save   (MA-11, MA-13)
 *   - fix-code:       POST /api/runs/{run_id}/fix-code         (MA-12)
 *
 * validate-save is the AUTHORITATIVE compile gate (MA-13): it runs BEFORE the
 * branch persist (`saveBranchAsync`). On `valid:false` the loop feeds the
 * proposed code to fix-code and re-validates; only a clean compile reaches the
 * branch save. The `runId` is threaded in by the caller (useMetadataEditorPanel).
 *
 * When there is NO run yet (`runId` is null/empty) we skip the compile gate and
 * persist the (already user-reviewed) code directly — the run-based endpoints
 * don't exist outside a run. The signature is kept identical to the dev-agent
 * original so the copied panel calls it unchanged.
 */
import type { Dispatch, SetStateAction } from "react";
import toast from "react-hot-toast";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { isHexDigit } from "@/services/multi-agent";
import type { MetadataBranch } from "@/services/metadata_v1/types";

const MAX_ATTEMPTS = 5;
const MAX_STUCK_BEFORE_BAIL = 3;

export type SaveErrorState = {
  message: string;
  code: string;
  /** Per-attempt error log for the end-of-loop review card (#12). */
  attempts?: { attempt: number; error: string }[];
  /** Banner tone. Defaults to error (red) for backward compatibility. "warning"
   *  (amber) = the save SUCCEEDED but the code could not be compile-validated
   *  (e.g. a new class + validator unavailable). */
  severity?: "error" | "warning";
} | null;

/**
 * Canonical UUID: 8-4-4-4-12 hex digits, hyphens at positions 8/13/18/23,
 * total length 36, case-insensitive.
 */
function isUuid(s: string): boolean {
  if (s.length !== 36) return false;
  for (let i = 0; i < 36; i++) {
    const ch = s[i];
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      if (ch !== "-") return false;
    } else if (!isHexDigit(ch)) {
      return false;
    }
  }
  return true;
}

export async function runSessionSaveWithValidation(params: {
  /** The active MULTI-AGENT run id. When set, validate-save runs as the compile
      gate before the branch persist (MA-11/MA-13); when null we skip the gate. */
  runId?: string | null;
  activeFileId: string;
  className: string;
  metadataType: string;
  content: string;
  /** Existing branch for this file, if any. When undefined, `saveBranchAsync`
      creates a new branch; when defined, it updates the existing one. */
  activeBranch?: MetadataBranch | null;
  /** Async form of the `useSaveBranchFile` mutation — persists code as a branch
      so it shows up in the deployment tab. */
  saveBranchAsync: (input: {
    metadataItemId: string;
    content: string;
    name?: string;
  }) => Promise<MetadataBranch>;
  /** When provided (file is virtual — not yet registered in ncoder-backend),
      used to create the metadata_item row before the branch save. */
  ensureMetadataItemId?: () => Promise<string | null>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveErrors: Dispatch<SetStateAction<SaveErrorState>>;
  setContentByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setDirtyByFileId: Dispatch<SetStateAction<Record<string, boolean>>>;
  setOriginalByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  onFileSaved?: (data: {
    id: string;
    fileName: string;
    metadataType: string;
  }) => void;
  /** When branch save used a canonical metadata UUID different from the tab id (virtual → real). */
  onTabIdResolved?: (canonicalId: string, previousTabId: string) => void;
  /** Returns true once the user has pressed Stop — the validate/auto-fix loop bails. */
  shouldAbort?: () => boolean;
  /** Aborts the IN-FLIGHT validate/fix request so Stop interrupts a slow call
   *  immediately instead of waiting for it to return. */
  abortSignal?: AbortSignal;
}): Promise<void> {
  const {
    runId,
    activeFileId,
    className,
    metadataType,
    content,
    activeBranch,
    saveBranchAsync,
    ensureMetadataItemId,
    setIsSaving,
    setSaveErrors,
    setContentByFileId,
    setDirtyByFileId,
    setOriginalByFileId,
    onFileSaved,
    onTabIdResolved,
    shouldAbort,
    abortSignal,
  } = params;

  // getState() — this module is a plain async function, not a component, so the
  // `useMultiAgentIdeStore(selector)` hook form would throw React #321.
  const ide = useMultiAgentIdeStore.getState();

  setIsSaving(true);
  setSaveErrors(null);

  // The code that ultimately persists — starts as the user content, replaced by
  // each accepted auto-fix while the compile gate loop runs.
  let currentCode = content;

  /** Persist `currentCode` as a branch and commit editor state. Shared by the
   *  no-run fast path and the post-validation success path. */
  const persistBranchAndCommit = async (): Promise<void> => {
    let persistKey = activeFileId;
    const saveTo = (itemId: string) =>
      saveBranchAsync({
        metadataItemId: itemId,
        content: currentCode,
        name: activeBranch ? undefined : `edit-${className}`,
      });
    try {
      let itemId = isUuid(activeFileId)
        ? activeFileId
        : await ensureMetadataItemId?.();
      if (!itemId) {
        throw new Error(
          "This file isn't registered yet and no create path is available.",
        );
      }
      try {
        await saveTo(itemId);
      } catch (firstErr) {
        // The tab id may be an ARTIFACT id (refactor opened via Compare), not a
        // real metadata_item — saving against it 404s with "Metadata item not
        // found". Resolve the item by CLASS NAME and retry once.
        const resolved = await ensureMetadataItemId?.();
        if (resolved && resolved !== itemId) {
          await saveTo(resolved);
          itemId = resolved;
        } else {
          throw firstErr;
        }
      }
      persistKey = itemId;
    } catch (branchErr: unknown) {
      const branchMsg =
        branchErr && typeof branchErr === "object" && "message" in branchErr
          ? String((branchErr as Error).message)
          : "Failed to save branch";
      if (!runId) {
        // No compile gate ran (no active run) — the branch persist IS the save,
        // so its failure is fatal.
        setSaveErrors({ message: `Branch save failed: ${branchMsg}`, code: currentCode });
        toast.error("Branch save failed");
        return;
      }
      // runId set → the native validate-save already pushed the validated code to
      // the org (the authoritative save). The branch persist is supplementary
      // versioning; don't block a successful org save on it. Commit the editor
      // state against the original id and warn instead of erroring.
      console.warn("[multi-agent save] branch persist failed after a successful validate-save:", branchMsg);
      persistKey = activeFileId;
    }

    const prevKey = activeFileId;
    setContentByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = currentCode;
      return next;
    });
    setDirtyByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = false;
      return next;
    });
    setOriginalByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = currentCode;
      return next;
    });
    if (persistKey !== prevKey) {
      onTabIdResolved?.(persistKey, prevKey);
      const prevBadge = ide.artifactBadges[prevKey];
      if (prevBadge) ide.setArtifactBadge(persistKey, prevBadge);
    }
    ide.setArtifactBadge(persistKey, "M");
    setSaveErrors(null);
    onFileSaved?.({ id: persistKey, fileName: className, metadataType });
  };

  try {
    // No run yet → no compile gate available; persist the user-reviewed code.
    if (!runId) {
      await persistBranchAndCommit();
      if (!useMultiAgentIdeStore.getState()) return; // defensive
      toast.success("Saved");
      return;
    }

    const { multiAgentValidateSave, multiAgentFixCode } = await import(
      "@/services/multi-agent"
    );
    let lastErrMsg = "";
    let stuckRuns = 0;
    // Surface the FIRST failing attempt's compile errors as a full (red) error
    // panel so the user sees what was actually wrong, even though the auto-fix
    // loop continues retrying below. Without this the only signal is the
    // truncated amber "Fixing…" line, which buries the real diagnostic.
    let firstErrorSurfaced = false;
    // #12 review card: record each failed attempt's compile error so the final
    // banner can show a fixed/pending/last-error summary across the 5 tries.
    const attemptLog: { attempt: number; error: string }[] = [];

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      // User pressed Stop → bail out of the validate/auto-fix loop. Nothing is
      // persisted on an aborted save; the last code stays in the editor.
      if (shouldAbort?.()) {
        setSaveErrors({ message: `Save stopped at attempt ${attempt}/${MAX_ATTEMPTS}.`, code: currentCode, attempts: attemptLog });
        return;
      }
      setSaveErrors({
        message:
          attempt === 1
            ? "Validating..."
            : `Re-validating (attempt ${attempt}/${MAX_ATTEMPTS})...`,
        code: currentCode,
      });

      // MA-13: validate-save is the authoritative compile gate.
      const result = await multiAgentValidateSave(runId, {
        class_name: className,
        content: currentCode,
        metadata_type: metadataType,
      }, abortSignal);

      // Stop pressed during the (slow) validate call → bail as soon as it returns,
      // before persisting or kicking off another auto-fix.
      if (shouldAbort?.()) {
        setSaveErrors({ message: `Save stopped at attempt ${attempt}/${MAX_ATTEMPTS}.`, code: currentCode, attempts: attemptLog });
        return;
      }

      if (result.valid) {
        // The backend's internal fix loop may have repaired the code — persist
        // the SERVER's validated content, not the original (otherwise a
        // non-compiling file ships to the branch). persistBranchAndCommit and
        // the editor commit below both read `currentCode`.
        if (result.content && result.content !== currentCode) {
          currentCode = result.content;
          setContentByFileId((prev) => ({ ...prev, [activeFileId]: currentCode }));
        }
        // Compile passed → persist as a branch.
        await persistBranchAndCommit();
        toast.success(
          attempt === 1
            ? "Validated and saved"
            : `Fixed and saved (attempt ${attempt})`,
        );
        return;
      }

      // NEW CLASS + no validator available. The Tooling API can't compile-check a
      // class that doesn't exist in the org yet, and the overlay validator (the
      // only thing that CAN check new code) was unavailable — so the backend
      // returns "unvalidated". This is NOT a compile error: feeding it to the
      // auto-fix loop just spins uselessly for MAX_ATTEMPTS and then hard-blocks
      // the save ("Could not fix compilation errors"), stranding the new class.
      // validate-save never deploys to the org (the IDE only persists a branch),
      // so persist the user-reviewed code and surface a NON-blocking warning.
      if (result.validation_status === "unvalidated") {
        if (result.content && result.content !== currentCode) {
          currentCode = result.content;
          setContentByFileId((prev) => ({ ...prev, [activeFileId]: currentCode }));
        }
        await persistBranchAndCommit();
        const why = (result.reason || "").trim();
        setSaveErrors({
          message: why
            ? `Saved without compile validation — ${why}. Review the code before deploying.`
            : "Saved without compile validation — the validator was unavailable. Review the code before deploying.",
          code: currentCode,
          severity: "warning",
        });
        toast("Saved (not compile-validated)", { icon: "⚠️" });
        return;
      }

      const errors = result.errors || [];
      if (errors.length === 0) {
        setSaveErrors({
          message:
            "Validation failed but the server did not return any errors. Try saving again or check the backend logs.",
          code: currentCode,
        });
        toast.error("Validation failed (no error details)");
        return;
      }

      const errMsg = errors
        .map((e) => {
          const line = e.line ? ` (line ${e.line})` : "";
          const msg = typeof e === "string" ? e : e.message || "Compilation error (no details)";
          return `${msg}${line}`;
        })
        .join("; ");
      lastErrMsg = errMsg;
      attemptLog.push({ attempt, error: errMsg });

      if (attempt >= MAX_ATTEMPTS) {
        setSaveErrors({
          message: `Failed after ${MAX_ATTEMPTS} attempts: ${errMsg}`,
          code: currentCode,
          attempts: attemptLog,
        });
        toast.error("Could not fix compilation errors");
        return;
      }

      // Show the original compile errors in the full (red) error panel on the
      // FIRST failure so the user sees what was actually wrong. Plain `errMsg`
      // (no "Fixing"/"Validating" prefix) renders as the detailed banner, and
      // because the fix call below is awaited, React renders this state while
      // the auto-fix runs. Subsequent attempts fall through to the amber
      // "Fixing…" status as before.
      if (!firstErrorSurfaced) {
        firstErrorSurfaced = true;
        setSaveErrors({ message: errMsg, code: currentCode });
      } else {
        setSaveErrors({
          message: `Fixing (attempt ${attempt}/${MAX_ATTEMPTS}): ${errMsg}`,
          code: currentCode,
        });
      }

      // MA-12: route auto-fix through the multi-agent run's fix-code endpoint.
      try {
        const fix = await multiAgentFixCode(runId, {
          class_name: className,
          content: currentCode,
          errors,
          metadata_type: metadataType,
        }, abortSignal);
        if (shouldAbort?.()) {
          setSaveErrors({ message: `Save stopped at attempt ${attempt}/${MAX_ATTEMPTS}.`, code: currentCode, attempts: attemptLog });
          return;
        }
        if (fix.fixed && fix.content && fix.content !== currentCode) {
          currentCode = fix.content;
          stuckRuns = 0;
        } else {
          stuckRuns += 1;
          if (stuckRuns >= MAX_STUCK_BEFORE_BAIL) {
            setSaveErrors({
              message: `Auto-fix is stuck after ${stuckRuns} attempts producing no change. Last error: ${errMsg}`,
              code: currentCode,
              attempts: attemptLog,
            });
            toast.error("Auto-fix is stuck — manual edit required");
            return;
          }
        }
      } catch (fixErr: unknown) {
        // Stop pressed mid-request → the fetch was aborted. Clean stop, not a failure.
        if (abortSignal?.aborted || (fixErr as { name?: string })?.name === "AbortError") {
          setSaveErrors({ message: `Save stopped at attempt ${attempt}/${MAX_ATTEMPTS}.`, code: currentCode, attempts: attemptLog });
          return;
        }
        stuckRuns += 1;
        const fixMsg =
          fixErr && typeof fixErr === "object" && "message" in fixErr
            ? String((fixErr as Error).message)
            : "";
        if (stuckRuns >= MAX_STUCK_BEFORE_BAIL) {
          setSaveErrors({
            message: `Auto-fix request failed ${stuckRuns} times in a row: ${fixMsg || errMsg}`,
            code: currentCode,
            attempts: attemptLog,
          });
          toast.error("Auto-fix request failed");
          return;
        }
        setSaveErrors({
          message: `Fix attempt ${attempt} failed (${fixMsg || "network"}), retrying…`,
          code: currentCode,
        });
      }
    }
    void lastErrMsg;
  } catch (err: unknown) {
    // Stop pressed during the validate request → the fetch was aborted. Treat as
    // a clean stop (no error toast), mirroring the in-loop abort checks.
    if (abortSignal?.aborted || (err as { name?: string })?.name === "AbortError") {
      setSaveErrors({ message: "Save stopped.", code: currentCode });
      return;
    }
    const msg =
      err && typeof err === "object" && "message" in err
        ? String((err as Error).message)
        : "Save failed";
    toast.error(msg);
    setSaveErrors({ message: msg || "Save failed", code: currentCode });
  } finally {
    setIsSaving(false);
  }
}
